/**
 * Classification for chat-stream errors so the SPA can decide whether a
 * "Reconnect" affordance is meaningful.
 *
 * Two error classes reach the assistant-ui error state:
 *
 *  - **Terminal server errors** — the turn died on the server (serialization
 *    crash / `internal_error`, `quota_exceeded`, `provider_billing_exhausted`,
 *    an unknown server `error` frame). Reconnecting replays nothing; the only
 *    sensible affordance is "Try again" (re-run the turn). These are modelled
 *    as a `ChatStreamError` with `recoverable: false`.
 *
 *  - **Transport drops** — an unwrapped network/fetch error escaping the
 *    stream. Here reconnecting (resume from the queue) is exactly right, so we
 *    keep the "Reconnect" button. Anything that is NOT a non-recoverable
 *    `ChatStreamError` is treated as transport/recoverable.
 *
 * The reader is intentionally duck-typed (`recoverable` own-property check)
 * rather than relying solely on `instanceof`, so the classification survives
 * any structured-clone / re-wrap across the runtime boundary.
 */
export class ChatStreamError extends Error {
  readonly code: string;
  readonly recoverable: boolean;
  /** Brand so the classifier can recognise the type after a re-wrap. */
  readonly isChatStreamError = true as const;

  constructor(code: string, message: string, recoverable: boolean) {
    super(message);
    this.name = "ChatStreamError";
    this.code = code;
    this.recoverable = recoverable;
    // Preserve prototype chain when transpiled to ES5-ish targets.
    Object.setPrototypeOf(this, ChatStreamError.prototype);
  }
}

/**
 * True when the error represents a recoverable transport drop (show
 * "Reconnect"); false when it is a terminal server error (show "Try again").
 */
export function isRecoverableStreamError(error: unknown): boolean {
  if (
    error != null &&
    typeof error === "object" &&
    "recoverable" in error &&
    typeof (error as { recoverable: unknown }).recoverable === "boolean"
  ) {
    return (error as { recoverable: boolean }).recoverable;
  }
  // Unknown / unwrapped errors are treated as transport drops — reconnect is
  // the safer default for those.
  return true;
}
